/**
 * TRTF product page — delivery estimate and size-guide hand-off.
 * ------------------------------------------------------------------
 * The delivery range is computed in the browser, not in Liquid, for one
 * reason: Shopify caches rendered Liquid, so a date baked in server-side goes
 * stale and starts quoting yesterday. Working it out at page load means the
 * range is always counted from today.
 *
 * The arithmetic is just the merchant's own stated policy — processing days
 * plus transit days, counted from the shopper's own clock, skipping weekends.
 * It is not a promise invented to create urgency, and if the settings are
 * blank nothing is displayed.
 */
(function () {
  /** Add n business days to a date, skipping Saturday and Sunday. */
  function addBusinessDays(from, n) {
    const d = new Date(from.getTime());
    let left = n;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) left--;
    }
    return d;
  }

  // Built part by part rather than handed to toLocaleDateString as a whole,
  // because dropping the month lets some locales reorder the rest and print
  // "24 Thu". Weekday first, then the date, every time.
  function fmt(d, withMonth) {
    const loc = (typeof navigator !== 'undefined' && navigator.language) || undefined;
    const weekday = d.toLocaleDateString(loc, { weekday: 'short' });
    const rest = withMonth
      ? d.toLocaleDateString(loc, { month: 'short', day: 'numeric' })
      : String(d.getDate());
    return weekday + ', ' + rest;
  }

  function renderDelivery(el) {
    const n = (k) => parseInt(el.dataset[k], 10);
    const procMin = n('procMin'), procMax = n('procMax');
    const shipMin = n('shipMin'), shipMax = n('shipMax');
    if ([procMin, procMax, shipMin, shipMax].some((v) => !isFinite(v))) return;

    // Counted on the store's own clock rather than the shopper's, because the
    // window is a promise TRTF makes: everyone sees the same dates wherever
    // they happen to be. Cut-off is 17:00 America/New_York — land before it
    // and the count starts today, land after it and it starts tomorrow.
    // Running in the browser means it is recounted on every visit rather
    // than baked into a cached render.
    let start = new Date();
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      const part = (t) => parseInt(parts.find((x) => x.type === t).value, 10);
      start = new Date(part('year'), part('month') - 1, part('day'));
      if (part('hour') >= 17) start.setDate(start.getDate() + 1);
    } catch (e) {}

    const earliest = addBusinessDays(start, procMin + shipMin);
    const latest = addBusinessDays(start, procMax + shipMax);

    const target = el.querySelector('[data-trtf-delivery-range]');
    if (!target) return;

    const sameMonth = earliest.getMonth() === latest.getMonth();
    target.textContent = earliest.getTime() === latest.getTime()
      ? fmt(latest, true)
      : fmt(earliest, !sameMonth) + ' – ' + fmt(latest, true);
  }

  /* ------------------------------------------------------------------ *
   * Info drawer
   *
   * One drawer, reused by every row that has content, built once and
   * portaled to <body>.
   *
   * <body> matters: the buy box is position:sticky, and sticky ALWAYS
   * creates a stacking context regardless of z-index. A drawer left inside
   * it renders behind the site header no matter how high you push z-index.
   * That is exactly what went wrong with the size guide, so this one starts
   * where it needs to end up.
   * ------------------------------------------------------------------ */
  let drawer = null;

  function buildDrawer() {
    if (drawer) return drawer;

    const scrim = document.createElement('div');
    scrim.className = 'trtf-info__scrim';

    const panel = document.createElement('div');
    panel.className = 'trtf-info__drawer';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.innerHTML =
      '<div class="trtf-info__head">' +
      '<h2 class="trtf-info__title" data-info-title></h2>' +
      '<button type="button" class="trtf-info__close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="trtf-info__body" data-info-body></div>';

    document.body.appendChild(scrim);
    document.body.appendChild(panel);

    scrim.addEventListener('click', closeDrawer);
    panel.querySelector('.trtf-info__close').addEventListener('click', closeDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });

    drawer = { scrim, panel, opener: null };
    return drawer;
  }

  function openDrawer(title, contentEl, opener) {
    const d = buildDrawer();
    d.opener = opener || null;
    d.panel.querySelector('[data-info-title]').textContent = title;

    const body = d.panel.querySelector('[data-info-body]');
    body.innerHTML = '';
    // Cloned, not moved: the source stays in the DOM so the content is still
    // crawlable and still there if the drawer is opened again.
    body.appendChild(contentEl.cloneNode(true));
    body.firstElementChild.hidden = false;

    d.scrim.classList.add('is-open');
    d.panel.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    d.panel.querySelector('.trtf-info__close').focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.scrim.classList.remove('is-open');
    drawer.panel.classList.remove('is-open');
    document.body.style.overflow = '';
    // Send focus back where it came from, or the shopper loses their place.
    if (drawer.opener) drawer.opener.focus();
  }

  /* ------------------------------------------------------------------ *
   * Gallery dots
   *
   * The theme's product media is a Swiper with slidesPerView 1. Above
   * 990px it also prints a thumbnail column, so the shopper can see there
   * is more than one image. Below 990px that column is display:none and
   * nothing replaces it - the gallery looks like a single static photo and
   * most people never swipe it.
   *
   * These dots exist only to say "there is more here". They are rendered
   * under the media, only below 990px (CSS), and only when there really is
   * more than one slide.
   *
   * Built on Swiper's public API - slideTo() and the slideChange event -
   * rather than on the theme's own pagination config, which points at a
   * container this theme never renders (.slider-pagiantion-bullet, the
   * typo is theirs). Driving it ourselves means a theme update that
   * renames that container cannot silently kill the dots.
   * ------------------------------------------------------------------ */
  function buildDots(swiperEl) {
    const swiper = swiperEl.swiper;
    if (!swiper || swiper.slides.length < 2) return false;

    const wrapper = swiperEl.closest('.product__media-wrapper')
      || swiperEl.parentElement;
    if (!wrapper || wrapper.querySelector('.trtf-dots')) return true;

    const nav = document.createElement('div');
    nav.className = 'trtf-dots';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Product images');

    const dots = [];
    for (let i = 0; i < swiper.slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'trtf-dots__dot';
      dot.setAttribute('role', 'tab');
      // Numbered, because "Image 3 of 9" is the only thing a dot can
      // usefully say to a screen reader.
      dot.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + swiper.slides.length);
      dot.addEventListener('click', () => swiper.slideTo(i));
      dots.push(dot);
      nav.appendChild(dot);
    }

    function sync() {
      const active = swiper.activeIndex;
      dots.forEach((d, i) => {
        const on = i === active;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    swiper.on('slideChange', sync);
    swiper.on('update', sync);
    sync();

    wrapper.appendChild(nav);
    return true;
  }

  /**
   * Swiper is initialised by the theme's own bundle, which may land after
   * this file. Rather than racing it, retry on a short leash and then stop
   * - a gallery with no dots is a mild loss, an interval that never dies is
   * not.
   */
  function initDots() {
    const el = document.querySelector('.product__media-list .swiper');
    if (!el) return;

    let tries = 0;
    (function attempt() {
      if (buildDots(el)) return;
      if (++tries > 20) return;
      setTimeout(attempt, 150);
    })();
  }

  function init() {
    document.querySelectorAll('[data-trtf-delivery]').forEach(renderDelivery);

    initDots();

    // Product details / Care: open the shared drawer with the hidden block
    // that sits immediately after the button.
    document.querySelectorAll('[data-trtf-info]').forEach((b) => {
      b.addEventListener('click', () => {
        const source = b.nextElementSibling;
        if (!source || !source.hasAttribute('data-info-source')) return;
        openDrawer(b.dataset.trtfInfo, source, b);
      });
    });

    // Size chart opens the existing size guide drawer rather than duplicating
    // the table, so the measurements only ever live in one place.
    document.querySelectorAll('[data-trtf-open-guide]').forEach((b) => {
      b.addEventListener('click', () => {
        const finder = document.querySelector('trtf-size-finder');
        const open = finder && finder.querySelector('[data-open="guide"]');
        if (open) open.click();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
